import { useCallback, useEffect, useState } from 'react';

/**
 * @typedef {Object} FitScaleResult
 * @property {(node: HTMLElement | null) => void} containerRef
 *   Callback ref for the element that defines the available space. Pass it
 *   as `ref={containerRef}` on the container (plain element or a styled
 *   component that forwards refs).
 * @property {number} scale
 *   Factor to apply as `transform: scale(scale)` on the fixed-size canvas so
 *   it fits the container on both axes. Starts at 1 until measured.
 * @property {boolean} isMeasured
 *   `true` once the container has been measured with a non-zero size. Use it
 *   to hide the canvas until the first real scale is known, so it doesn't
 *   flash at scale 1 first.
 */

/**
 * Computes the largest scale at which a fixed-size canvas fits inside its
 * container without overflowing on either axis ("contain" fit), and keeps it
 * up to date as the container resizes.
 *
 * How it works:
 * 1. The caller attaches `containerRef` to the element that represents the
 *    space the canvas may occupy.
 * 2. A `ResizeObserver` reports that element's content box whenever it
 *    changes size, including the first report right after observing.
 * 3. The scale is `min(availableWidth / canvasWidth, availableHeight / canvasHeight)`,
 *    so the canvas grows until whichever edge it reaches first.
 *
 * Layout requirement: the canvas must not take part in the container's
 * layout (e.g. `position: absolute` inside a `position: relative`
 * container). `transform: scale()` changes only how the canvas is painted,
 * not the space it occupies, so a canvas left in normal flow keeps its
 * unscaled size in the layout. That can stretch the container to the
 * canvas's height, and the measured height then no longer reflects the
 * space actually available. The container itself needs a definite size from
 * its own layout (for example `flex: 1; min-height: 0`, or `height: 100%` of
 * a sized parent).
 *
 * @param {number} canvasWidth - The canvas's logical (unscaled) width, in px.
 * @param {number} canvasHeight - The canvas's logical (unscaled) height, in px.
 * @returns {FitScaleResult}
 *
 * @example
 * const { containerRef, scale } = useFitScale(486, 495);
 *
 * <div ref={containerRef} style={{ position: 'relative', height: '100%', overflow: 'hidden' }}>
 *   <div
 *     style={{
 *       position: 'absolute',
 *       top: 0,
 *       left: '50%',
 *       width: 486,
 *       height: 495,
 *       transformOrigin: 'top center',
 *       transform: `translateX(-50%) scale(${scale})`,
 *     }}
 *   >
 *     {canvasContent}
 *   </div>
 * </div>
 */
export const useFitScale = (canvasWidth, canvasHeight) => {
  // The container is kept in state (via a callback ref) rather than in a
  // ref object, so the effect below re-runs when the element mounts late,
  // e.g. after a loading state, or when it is swapped for a new element.
  const [container, setContainer] = useState(null);
  const [scale, setScale] = useState(1);
  const [isMeasured, setIsMeasured] = useState(false);

  const containerRef = useCallback((node) => {
    setContainer(node);
  }, []);

  useEffect(() => {
    if (!container || !canvasWidth || !canvasHeight) {
      return undefined;
    }

    const updateScale = (availableWidth, availableHeight) => {
      // A zero size means the container isn't laid out yet (hidden, or not
      // attached to the page). Keep the last scale instead of collapsing the
      // canvas to 0.
      if (!availableWidth || !availableHeight) {
        return;
      }

      const nextScale = Math.min(availableWidth / canvasWidth, availableHeight / canvasHeight);

      // Only update when the value actually changes, to avoid re-rendering
      // the consumer on every resize notification.
      setScale((prevScale) => (prevScale === nextScale ? prevScale : nextScale));
      setIsMeasured(true);
    };

    // `contentRect` is the content box: it excludes padding and borders, so
    // a padded container yields the space actually free for the canvas.
    const observer = new ResizeObserver(([entry]) => {
      updateScale(entry.contentRect.width, entry.contentRect.height);
    });
    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, [container, canvasWidth, canvasHeight]);

  return { containerRef, scale, isMeasured };
};

export default useFitScale;
